/* eslint-disable no-shadow */
import SegmentCollectionModel from '@/common/models/segment-collection-model';
import SegmentModel from '@/common/models/segment-model';
import SegmentDataModel from '@/common/models/segment-data-model';

let segmentToAPIFilterString;
export const dependencies = async ({
    injectSegmentToAPIFilterString = null,
} = {}) => {
    if (injectSegmentToAPIFilterString) {
        segmentToAPIFilterString = injectSegmentToAPIFilterString;
    } else {
        const dependency = () => import('@/modules/segments/store-helpers');
        const resolvedDependency = await dependency();
        // eslint-disable-next-line prefer-destructuring
        segmentToAPIFilterString = resolvedDependency.default.segmentToAPIFilterString;
    }
};

export const state = {
    isSegmentsColumnOpen: true,
    segmentCollection: new SegmentCollectionModel(),
    activeFilters: null,
    hasNoPermissions: false,
    exportSort: null,
};

export const getters = {
    isSegmentsColumnOpen(state) {
        return state.isSegmentsColumnOpen;
    },
    segmentCollection(state) {
        return state.segmentCollection;
    },
    activeSegment(state) {
        return state.segmentCollection.activeSegment;
    },
};

export const mutations = {
    setSegments(state, segments) {
        state.segmentCollection.hydrate({
            // Keep active page
            activePageName: state.segmentCollection.activePageName,
            segments,
        });
    },
    addSegment(state, segment) {
        state.segmentCollection.addSegment(segment);
    },
    updateSegment(state, {segment, data}) {
        state.segmentCollection.updateSegment(segment, data);
    },
    deleteSegment(state, {id, update = false}) {
        state.segmentCollection.removeSegment(id, update);
    },
    /**
     * Run the mutation to set the active segment ID in the store.
     * @param state
     * @param {string} id
     */
    setActiveSegmentId(state, id) {
        state.segmentCollection.selectSegment(id);
    },
    setSegmentPanelVisibility(state, value) {
        state.isSegmentsColumnOpen = value;
    },
    /**
     * Update the search query for filtering segments in the segment column.
     * @param state
     * @param {string} query
     */
    setSearchQuery(state, query) {
        state.segmentCollection.activeCategory.searchQuery = query;
    },
    setActiveFilters(state, filters) {
        state.activeFilters = filters;
    },
    setExportSort(state, sortData) {
        state.exportSort = sortData;
    },
    activateSegmentPage(state, pageName) {
        state.segmentCollection.selectPage(pageName);
    },
    activateSegmentCategory(state, categoryName) {
        state.segmentCollection.activePage.selectCategory(categoryName);
    },
    toggleHiddenSegments(state) {
        state.segmentCollection.activeCategory.toggleHiddenSegments();
    },
    setNoPermissionsFlag(state, flag) {
        state.hasNoPermissions = flag;
    },
};

export const actions = ({api}) => {
    async function patchSegment(id, data) {
        return api(({segments}) => segments.patch({id, data}));
    }

    return {
        async fetch({commit}, auth) {
            try {
                // reset no permission flag
                commit('setNoPermissionsFlag', false);
                const {items} = await api(({segments}) => segments.getAll());
                // get type permissions
                const resources = Object.entries(SegmentDataModel.TypesAuth)
                    .reduce((result, [type, resource]) => {
                        result[type] = auth[resource];
                        return result;
                    }, {});
                const segments = [];
                items.forEach(({fields}) => {
                    const segment = new SegmentModel(fields);
                    // only save those allowed for the user
                    if (resources[segment.data.type]
                        && resources[segment.data.type].read.isAllowed) {
                        // check for type before trying to display, old types are broken
                        segments.push(segment);
                    }
                });
                if (items.length > 0 && segments.length === 0) {
                    console.error('User has no permissions to any segment entity types');
                    commit('setNoPermissionsFlag', true);
                }
                commit('setSegments', segments);
            } catch (err) {
                console.error(err);
            }
        },
        fetchSegmentsCount({dispatch}, segments) {
            if (!segments) {
                throw new Error('Must send store -> segments/fetchSegmentsCount segments to fetch');
            }
            let segmentsCollection = segments;
            if (!Array.isArray(segmentsCollection)) {
                segmentsCollection = [segmentsCollection];
            }
            segmentsCollection
                .filter(segment => segment.$recordCount === null)
                .forEach(segment => dispatch('updateSegmentCount', segment));
        },
        async updateSegmentCount({commit, rootGetters, rootState}, segment) {
            let resp;
            const filter = segmentToAPIFilterString(segment, {getters: rootGetters});
            const extraFilter = segment.data.page === SegmentDataModel.Pages.customerDetails
            && rootState.customers.customer
                ? `customerId:${rootState.customers.customer.id}`
                : ``;
            const params = {
                limit: 1,
                offset: 0,
                filter: filter ? `${filter};${extraFilter}` : extraFilter,
            };
            switch (segment.data.type) {
                case SegmentDataModel.Types.couponRedemptions:
                    resp = await api(({coupons}) => coupons.getAllRedemptions(params));
                    break;
                case SegmentDataModel.Types.apiLogs:
                    resp = await api(({tracking}) => tracking.getAllApiLogs(params));
                    break;
                case SegmentDataModel.Types.webhookLogs:
                    resp = await api(({tracking}) => tracking.getAllWebhookTrackingLogs(params));
                    break;
                default:
                    resp = await api(client => client[segment.data.type].getAll(params));
                    break;
            }
            const {total} = resp;
            commit('updateSegment', {segment, data: {$recordCount: total}});
        },
        updateSegmentCountFromProvider({commit}, {segment, total}) {
            commit('updateSegment', {segment, data: {$recordCount: total}});
        },
        async add({commit, dispatch}, segment) {
            const data = segment.getPayload();
            const {fields} = await api(({segments}) => segments.create({data}));
            segment = new SegmentModel(fields);
            commit('addSegment', segment);
            dispatch('updateSegmentCount', segment);
            return segment;
        },
        async toggleProperty({commit}, {id, isStarred, isVisible}) {
            // patching the segment requires both the starred and visible state
            // in the payload
            const {fields} = await patchSegment(id, {isStarred, isVisible});
            commit('deleteSegment', {id});
            commit('addSegment', new SegmentModel(fields));
        },
        async toggleVisibility({dispatch}, {id, isStarred, isVisible}) {
            await dispatch('toggleProperty', {id, isVisible: !isVisible, isStarred});
        },
        async toggleStarred({dispatch}, {id, isStarred, isVisible}) {
            await dispatch('toggleProperty', {id, isStarred: !isStarred, isVisible});
        },
        async update({commit, getters, dispatch}, segment) {
            const {id} = segment;
            segment = new SegmentModel({
                ...(getters.segmentCollection.findSegmentById(id) || {}),
                ...segment,
            });
            const data = segment.getPayload();
            const {fields} = await api(({segments}) => segments.update({id, data}));
            commit('deleteSegment', {id, update: true});
            commit('addSegment', new SegmentModel(fields));
            dispatch('updateSegmentCount', segment);
        },
        async delete({commit}, id) {
            await api(({segments}) => segments.delete({id}));
            commit('deleteSegment', {id});
        },
        setActiveSegmentId({commit}, id) {
            commit('setActiveSegmentId', id);
        },
        createTemporaryLogSegment(
            {commit, dispatch, rootGetters},
            {type, resourceId, gridColumns},
        ) {
            const segment = SegmentModel.createTemporarySegment({
                type,
                resourceId,
                ownerId: rootGetters['user/userId'],
                gridColumns,
            });
            try {
                // delete previous segment if present
                commit('deleteSegment', {id: segment.id, update: true});
            } catch (err) {
                // ignore
            }
            commit('addSegment', segment);
            commit('setActiveSegmentId', segment.id);
            dispatch('updateSegmentCount', segment);
        },
        activateSegmentPage({commit}, pageName) {
            commit('activateSegmentPage', pageName);
            commit('setSegmentPanelVisibility', true);
        },
        activateSegmentCategory({state, commit, dispatch}, categoryName) {
            commit('activateSegmentCategory', categoryName);
            commit('setSegmentPanelVisibility', true);

            let categorySegments = [];
            if (state.segmentCollection.activeCategory) {
                categorySegments = [
                    ...state.segmentCollection.activeCategory.$visibleSegments,
                    ...state.segmentCollection.activeCategory.$hiddenSegments,
                ];
            }

            dispatch('fetchSegmentsCount', categorySegments);
        },
        toggleSegmentsColumn({state, commit}) {
            commit('setSegmentPanelVisibility', !state.isSegmentsColumnOpen);
        },
        updateSearchQuery({commit}, query) {
            commit('setSearchQuery', query);
        },
        updateActiveFilters({commit}, filters) {
            commit('setActiveFilters', filters);
        },
        updateExportSort({commit}, sortData) {
            commit('setExportSort', sortData);
        },
        toggleHiddenSegments({commit}) {
            commit('toggleHiddenSegments');
        },
    };
};
